/**
 * Builds a multi-sheet .xlsx for price-list import (OOXML, store-only ZIP).
 * Header row only — no sample data. Rule is always FIXED on import.
 */

interface SheetDef {
  name: string;
  headers: string[];
  /** Extra rows for Hướng dẫn (optional). */
  rows?: string[][];
}

const GUIDE: SheetDef = {
  name: 'Hướng dẫn',
  headers: ['Mục', 'Nội dung'],
  rows: [
    ['Cách sử dụng', 'Điền dữ liệu vào các sheet tương ứng (Giá vốn, Thanh lý, Bán lẻ, …). Không sửa tên sheet và tên cột.'],
    ['Quy tắc giá', 'Khi import, hệ thống luôn lưu quy tắc "Số tiền cố định" (FIXED). Không nhập ruleType.'],
    ['Thời gian', 'Định dạng: dd/MM/yyyy HH:mm — ví dụ 21/10/2026 23:00. Thời gian kết thúc có thể để trống.'],
    ['Số tiền', 'Số không âm. Đơn vị theo bảng giá (thường VND).'],
    [
      'Phạm vi áp dụng (Thanh lý)',
      'Một trong: "Tất cả Cửa hàng và Website" | "Website" | "Chọn Cửa hàng cụ thể" (hoặc mã ALL_STORES_AND_WEB / WEB_ONLY / SELECTED_STORES).',
    ],
    [
      'Mã cửa hàng (Thanh lý)',
      'Chỉ bắt buộc khi Phạm vi = "Chọn Cửa hàng cụ thể". Nhiều mã cách nhau bởi dấu chấm phẩy (;), ví dụ SHOP01;SHOP02.',
    ],
    ['Lý do (Thanh lý)', 'Hệ thống tự gán "Thanh lý tồn kho" (CLEARANCE_STOCK). Các sheet khác để trống lý do.'],
    [
      'Giá vốn',
      'Chỉ nhập Mã SKU và Số tiền (lớn hơn 0). Hệ thống lấy thời điểm import làm thời gian bắt đầu và không đặt thời gian kết thúc. Không nhập nhà cung cấp; dòng được ghi là vốn chính.',
    ],
  ],
};

const DATA_SHEETS: SheetDef[] = [
  {
    name: 'Giá vốn',
    headers: ['Mã SKU', 'Số tiền'],
  },
  {
    name: 'Thanh lý',
    headers: [
      'Mã SKU',
      'Thời gian bắt đầu',
      'Thời gian kết thúc',
      'Số tiền',
      'Phạm vi áp dụng',
      'Mã cửa hàng',
    ],
  },
  {
    name: 'Bán lẻ',
    headers: ['Mã SKU', 'Thời gian bắt đầu', 'Thời gian kết thúc', 'Số tiền'],
  },
  {
    name: 'Website',
    headers: ['Mã SKU', 'Thời gian bắt đầu', 'Thời gian kết thúc', 'Số tiền'],
  },
  {
    name: 'Cửa hàng',
    headers: ['Mã SKU', 'Thời gian bắt đầu', 'Thời gian kết thúc', 'Số tiền'],
  },
  {
    name: 'Sỉ',
    headers: [
      'Mã SKU',
      'Thời gian bắt đầu',
      'Thời gian kết thúc',
      'Số tiền',
      'Số lượng tối thiểu',
    ],
  },
  {
    name: 'Phân phối',
    headers: ['Mã SKU', 'Thời gian bắt đầu', 'Thời gian kết thúc', 'Số tiền'],
  },
  {
    name: 'Thị trường',
    headers: ['Mã SKU', 'Thời gian bắt đầu', 'Thời gian kết thúc', 'Số tiền'],
  },
];

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sheetXml(sheet: SheetDef): string {
  const allRows = [sheet.headers, ...(sheet.rows ?? [])];
  const rowXml = allRows
    .map((cells, rowIndex) => {
      const cellXml = cells
        .map((cell, colIndex) => {
          const ref = `${colName(colIndex)}${rowIndex + 1}`;
          return `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(cell)}</t></is></c>`;
        })
        .join('');
      return `<row r="${rowIndex + 1}">${cellXml}</row>`;
    })
    .join('');
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetData>${rowXml}</sheetData></worksheet>`
  );
}

function colName(index: number): string {
  let n = index;
  let name = '';
  do {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return name;
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i];
    for (let j = 0; j < 8; j++) {
      const mask = -(crc & 1);
      crc = (crc >>> 1) ^ (0xedb88320 & mask);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u16(value: number): Uint8Array {
  const out = new Uint8Array(2);
  out[0] = value & 0xff;
  out[1] = (value >>> 8) & 0xff;
  return out;
}

function u32(value: number): Uint8Array {
  const out = new Uint8Array(4);
  out[0] = value & 0xff;
  out[1] = (value >>> 8) & 0xff;
  out[2] = (value >>> 16) & 0xff;
  out[3] = (value >>> 24) & 0xff;
  return out;
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

function encodeUtf8(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

function zipStore(files: { path: string; content: string }[]): Blob {
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;
  for (const file of files) {
    const nameBytes = encodeUtf8(file.path);
    const data = encodeUtf8(file.content);
    const crc = crc32(data);
    const localHeader = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      data,
    ]);
    const centralHeader = concatBytes([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]);
    localParts.push(localHeader);
    centralParts.push(centralHeader);
    offset += localHeader.length;
  }
  const central = concatBytes(centralParts);
  const end = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(central.length),
    u32(offset),
    u16(0),
  ]);
  return new Blob([concatBytes([...localParts, central, end])], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function buildPriceImportTemplateXlsx(): Blob {
  const sheets = [GUIDE, ...DATA_SHEETS];
  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    sheets
      .map(
        (_, i) =>
          `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
      )
      .join('') +
    `</Types>`;

  const rootRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`;

  const workbook =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets>` +
    sheets
      .map(
        (sheet, i) =>
          `<sheet name="${xmlEscape(sheet.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`,
      )
      .join('') +
    `</sheets></workbook>`;

  const workbookRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    sheets
      .map(
        (_, i) =>
          `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
      )
      .join('') +
    `</Relationships>`;

  const files = [
    { path: '[Content_Types].xml', content: contentTypes },
    { path: '_rels/.rels', content: rootRels },
    { path: 'xl/workbook.xml', content: workbook },
    { path: 'xl/_rels/workbook.xml.rels', content: workbookRels },
    ...sheets.map((sheet, i) => ({
      path: `xl/worksheets/sheet${i + 1}.xml`,
      content: sheetXml(sheet),
    })),
  ];
  return zipStore(files);
}
